import DB from '../db/DB';
import type { SocialGraphInteraction, SocialGraphNonPlayer } from '../ReplicantSocialGraph';
import { deepEquals } from '../utils/ObjUtils';

export class ServerReplicantSocialGraph {
    constructor(private db: DB<any>) {}

    async updatePlayer(opts: {
        playerId: string;
        nonPlayerFriendIds: string[];
        meta: { [key: string]: unknown };
    }): Promise<void> {
        const { playerId, meta, nonPlayerFriendIds } = opts;

        // 1. Read player item and keep track of the previously stored values:
        const player = await this.db.socialGraph.getPlayer(playerId);

        // 2. Update non-player items if player added or removed friends since previous update:
        const nonPlayerUpdatePromises: Promise<void>[] = [];
        const oldNonPlayerIds = player?.nonPlayerFriendIds ?? [];

        for (const nonPlayerId of [...oldNonPlayerIds, ...nonPlayerFriendIds]) {
            if (!oldNonPlayerIds.includes(nonPlayerId)) {
                // Update newly added non-player friend:
                nonPlayerUpdatePromises.push(this.db.socialGraph.addPlayerFriend(nonPlayerId, playerId));
            } else if (!nonPlayerFriendIds.includes(nonPlayerId)) {
                // Update removed non-player friend:
                nonPlayerUpdatePromises.push(this.db.socialGraph.removePlayerFriend(nonPlayerId, playerId));
            }
        }

        await Promise.all(nonPlayerUpdatePromises);

        // 3. Update player item if values changed since previous update (block until non-player item updates resolve):
        if (!player || !deepEquals(player.meta, meta) || !deepEquals(player.nonPlayerFriendIds, nonPlayerFriendIds)) {
            await this.db.socialGraph.createOrUpdatePlayer({ id: playerId, nonPlayerFriendIds, meta });
        }
    }

    async trackInteractions(opts: { senderId: string; interactions: SocialGraphInteraction[] }): Promise<void> {
        const { senderId, interactions } = opts;

        // 1. Group interactions by receiver (non-player friend):
        const interactionsByReceiver: { [receiverId: string]: SocialGraphInteraction[] } = {};
        for (const interaction of interactions) {
            if (!(interaction.receiverId in interactionsByReceiver)) {
                interactionsByReceiver[interaction.receiverId] = [];
            }

            interactionsByReceiver[interaction.receiverId]!.push(interaction);
        }

        // 2. Write interactions into DB per group:
        await Promise.all(
            Object.entries(interactionsByReceiver).map(([receiverId, interactions]) =>
                this.db.socialGraph.createInteractions(
                    receiverId,
                    interactions.map(({ meta, timestamp, type }) => ({ meta, senderId, timestamp, type })),
                ),
            ),
        );
    }

    async getNonPlayers(nonPlayerIds: string[]): Promise<SocialGraphNonPlayer[]> {
        // 1. Get non-player items:
        const nonPlayers = await this.db.socialGraph.getNonPlayers(nonPlayerIds);

        // 2. Get player items for all the non-players' player friends:
        const uniquePlayerIds = [...new Set(nonPlayers.flatMap((nonPlayer) => nonPlayer.playerFriendIds))];
        const players = await this.db.socialGraph.getPlayers(uniquePlayerIds);

        // 3. Combine non-player and player items:
        return nonPlayers.map(({ id, interactions, playerFriendIds }) => ({
            id,
            interactions,
            playerFriends: playerFriendIds
                .filter((playerId) => playerId in players)
                .map((playerId) => ({ id: playerId, meta: players[playerId]!.meta })),
        }));
    }
}
